import React, { useState } from 'react';
import {
  Link,
  Button,
  ButtonGroup,
  Currency,
  Divider,
  Drawer,
  HistoryDropDown,
  Modal,
  TimeWithTimeZone,
  Col,
  Row,
  Text,
  Title,
} from '@common/components';

import { useMutation, useQuery } from '@common/hooks';
import { DeleteHourBlock, ReviewHourBlockInfo } from '@timekeeping/graphql';
import {
  EMPTY_PLACEHOLDER,
  formatDateInTz,
  formatSecondsToDuration,
  formatSummaryDate,
  formatZoneAbbreviation,
  parseInTz,
} from '@timekeeping/services';
import { createMessage } from 'works/legacy/lib/message-helper';
import { buildDialogUrl } from '@common/services';

export const HourBlockReviewDrawer = ({
  open,
  onClose,
  hourBlockId = '',
}: {
  open: boolean;
  onClose: () => void;
  hourBlockId?: string;
}) => {
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const { loading, data } = useQuery(ReviewHourBlockInfo, {
    skip: !hourBlockId,
    variables: { id: hourBlockId },
  });

  const [performDeleteAction] = useMutation(DeleteHourBlock, {
    onCompleted: () => {
      createMessage('success', 'Hours deleted.');
      setDeleteLoading(false);
      setConfirmVisible(false);
    },
    onError: () => {
      createMessage(
        'error',
        'An error occurred while trying to delete hours. Please, try again.'
      );
      setDeleteLoading(false);
      setConfirmVisible(false);
    },
  });

  const onDeleteConfirm = () => {
    setDeleteLoading(true);
    performDeleteAction({
      variables: { id: hourBlockId! },
    });
  };

  const hourBlock = data?.hourBlockInfo?.data;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={
          hourBlock
            ? formatSummaryDate(
                parseInTz(
                  hourBlock.inOut.startedAt!,
                  hourBlock.inOut.startedAtTz
                )
              )
            : undefined
        }
        footer={
          <ButtonGroup>
            {hourBlock?.canSoftDelete.value &&
              hourBlock?.state !== 'deleted' && (
                <Button
                  kind="tertiary"
                  danger
                  className="hour-block-drawer-delete-button"
                  onClick={() => setConfirmVisible(true)}
                  disabled={loading}
                >
                  Delete
                </Button>
              )}
            {hourBlock?.workerTimesheet.canManagerUpsertHours.value &&
              hourBlock?.state !== 'deleted' && (
                <Link
                  to={buildDialogUrl('timekeeping-hour-block-edit-drawer', {
                    props: { hourBlockId },
                    overlap: true,
                  })}
                >
                  <Button
                    kind="primary"
                    className="hour-block-drawer-edit-button"
                    disabled={loading}
                  >
                    Edit
                  </Button>
                </Link>
              )}
            {hourBlock?.workerTimesheet.canManagerUpsertHours.value &&
              hourBlock?.state === 'deleted' && (
                <Link
                  to={buildDialogUrl('timekeeping-hour-block-restore-drawer', {
                    props: { hourBlockId },
                    overlap: true,
                  })}
                >
                  <Button
                    className="hour-block-drawer-restore-button"
                    disabled={loading}
                  >
                    Restore
                  </Button>
                </Link>
              )}
          </ButtonGroup>
        }
        loading={loading}
      >
        {hourBlock && (
          <Row gutter={[0, 18]}>
            <Col span={24}>
              <Row gutter={[0, 4]}>
                <Col span={24}>
                  <Title size="h4" weight="bold">
                    Location
                  </Title>
                </Col>
                <Col span={24}>
                  <Row gutter={[0, 12]}>
                    <Col span={6}>
                      <Text secondary>Facility</Text>
                    </Col>
                    <Col span={18}>{hourBlock.facility!.name}</Col>
                    <Col span={6}>
                      <Text secondary>Unit</Text>
                    </Col>
                    <Col span={18}>{hourBlock.unit!.name}</Col>
                    <Col span={6}>
                      <Text secondary>Cost Center</Text>
                    </Col>
                    <Col span={18}>{hourBlock.unit!.costCenter}</Col>
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Divider />
            </Col>
            <Col span={24}>
              <Row gutter={[0, 4]}>
                <Col span={24}>
                  <Title size="h4" weight="bold">
                    Hours
                  </Title>
                </Col>
                <Col span={24}>
                  <Row gutter={[0, 12]}>
                    <Col span={6}>
                      <Text secondary>Date Worked</Text>
                    </Col>
                    <Col span={18}>
                      <TimeWithTimeZone
                        date={parseInTz(
                          hourBlock.inOut.startedAt!,
                          hourBlock.inOut.startedAtTz
                        ).toISOString()}
                        variant="short_format"
                      />
                    </Col>
                    <Col span={6}>
                      <Text secondary>Hour Type</Text>
                    </Col>
                    <Col span={18}>
                      <Text>{hourBlock.hourType!.name}</Text>
                    </Col>
                    <Col span={6}>
                      <Text secondary>Clock In</Text>
                    </Col>
                    <Col span={18}>
                      <Text>
                        {formatDateInTz(
                          hourBlock.inOut.startedAt!,
                          hourBlock.inOut.startedAtTz
                        )}
                      </Text>
                    </Col>
                    <Col span={6}>
                      <Text secondary>Clock Out</Text>
                    </Col>
                    <Col span={18}>
                      <Text>
                        {formatDateInTz(
                          hourBlock.inOut.endedAt!,
                          hourBlock.inOut.endedAtTz
                        )}
                      </Text>
                    </Col>
                    <Col span={6}>
                      <Text secondary>Break Start</Text>
                    </Col>
                    <Col span={18}>
                      <Text>
                        {hourBlock.lunchBreak
                          ? formatDateInTz(
                              hourBlock.lunchBreak.startedAt!,
                              hourBlock.lunchBreak.startedAtTz
                            )
                          : EMPTY_PLACEHOLDER}
                      </Text>
                    </Col>
                    <Col span={6}>
                      <Text secondary>Break End</Text>
                    </Col>
                    <Col span={18}>
                      <Text>
                        {hourBlock.lunchBreak
                          ? formatDateInTz(
                              hourBlock.lunchBreak.endedAt!,
                              hourBlock.lunchBreak.endedAtTz
                            )
                          : EMPTY_PLACEHOLDER}
                      </Text>
                    </Col>
                    <Col span={6}>
                      <Text secondary>Total Time</Text>
                    </Col>
                    <Col span={18}>
                      {formatSecondsToDuration(hourBlock.totalSeconds)}
                    </Col>
                    <Col span={6}>
                      <Text secondary>Rate</Text>
                    </Col>
                    <Col span={18}>
                      {hourBlock.billingRate ? (
                        <Currency value={hourBlock.billingRate} />
                      ) : (
                        EMPTY_PLACEHOLDER
                      )}
                    </Col>
                    {hourBlock.inOut.startedAtTz &&
                      hourBlock.inOut.startedAt && (
                        <Col span={24}>
                          <Text>
                            *All times in{' '}
                            {formatZoneAbbreviation(
                              hourBlock.inOut.startedAt,
                              hourBlock.inOut.startedAtTz
                            )}
                          </Text>
                        </Col>
                      )}
                  </Row>
                </Col>
              </Row>
            </Col>
            {hourBlock.notes && (
              <>
                <Col span={24}>
                  <Divider />
                </Col>
                <Col span={24}>
                  <Row gutter={[0, 8]}>
                    <Col span={24}>
                      <Title size="h4" weight="bold">
                        Worker Note
                      </Title>
                    </Col>
                    <Col span={24}>
                      <Text>{hourBlock.notes}</Text>
                    </Col>
                  </Row>
                </Col>
              </>
            )}
            <Col span={24}>
              <HistoryDropDown data={hourBlock.history || []} />
            </Col>
          </Row>
        )}
      </Drawer>
      <Modal
        visible={confirmVisible}
        title="Are you sure you want to delete?"
        onCancel={() => setConfirmVisible(false)}
        footer={
          <ButtonGroup align="right">
            <Button
              kind="tertiary"
              size="md"
              className="hour-block-delete-action-cancel"
              onClick={() => setConfirmVisible(false)}
              disabled={deleteLoading}
            >
              No, Cancel
            </Button>
            <Button
              kind="primary"
              size="md"
              className="hour-block-delete-action-confirm"
              onClick={onDeleteConfirm}
              loading={deleteLoading}
            >
              Yes, Delete
            </Button>
          </ButtonGroup>
        }
      >
        You&apos;ll be able to undo deleting up until you approve this
        timesheet.
      </Modal>
    </>
  );
};
